import { DropDownList, DropDownListProps } from "./dropDownList.js";
import { FullHeightContainer } from "./FullHeighContainer.js";
import { ToolBar, ToolBarCustomButton } from "./toolBar.js";
import { Helper } from "../util/helper.js";
import { HTTPService, JsonResultClass } from "../util/httpManager.js";
import { CommandManager } from "../util/commandManager.js";
import { Config } from "../config/config.js";
import { FieldInstance } from "./field.js";
import { NotificationHelper } from "../util/notificationHelper.js";
import { Dialog, DialogProps } from "./dialog.js";
import { FileUpload } from "./fileUpload.js";
import { SkillsMatrix } from "./skills/skillsMatrix.js";
import { SkillEditBoxProps } from "./skills/skillEditBox.js";
import { SkillHelper } from "../../generalSettings/skills/skillsHelper.js";
import { SkillSettings, SkillTreeApp } from "../../skills/skillTreeApp.js";
import { SkillCategory, SkillFilter, SkillsState, TreeNodeVm } from "../../generalSettings/skills/skillsInterfaces.js";
import { Entity } from "./commonApp.js";
import React from "react";
import ReactDOM from "react-dom";

export default class Skills extends React.Component<SkillsProps, SkillsState> {
    private _skillsLoaded = false;
    private _filledSkillColor = "#C8FFA8";
    private _useSkillProfile = true;
    private _skillProfile = null;
    private _maschNr: string = null;
    private _pos: number = 1;
    private _canWrite: boolean = false;
    private _matrixApp: SkillsMatrix = null;
    private _canLoadSkills = false;
    private _skillSettings: SkillSettings = {};
    private _afterLoadCallback: (skills: Array<SkillCategory>) => void = null;
    private _internalAfterLoadCallback: () => void = null;
    private readOnly: boolean = true;
    private dynamicSearch: boolean = false;
    private treeRef: SkillTreeApp;

    private _dialogProps: DialogProps = {
        title: "",
        actions: [
            {
                label: Config.translations.cancel,
                action: () => { }
            },
            {
                label: Config.translations.ok,
                action: () => {
                    if (this.state.file != null) {
                        this.upload(this.state.file);
                    }
                },
                primary: true
            }
        ],
        afterClose: () => {
            this.setState({ loadSkillsDialogOpen: false, file: null });
        },
        width: 425


    };

    constructor(props: SkillsProps) {
        super(props);
        this.readOnly = !props.entity.RecordRight.CanWrite;
        this.state = {
            editMode: false,
            loadSkillsDialogOpen: false,
            file: null
        };
        this.dynamicSearch = Helper.getValueOrDefault(props.dynamicSearch, false);
        this._initApp();
    }

    private _getMaschNr = (): { maschNr: string, pos?: number, maschTyp?: string } => {
        const ctx = this.props.runningContext.toLowerCase();
        const result = { maschNr: null, pos: null, maschTyp: null };
        switch (ctx) {
            case "address":
            case "machine":
            case "ticket":
            case "resource":
            case "project":
                result.maschNr = this.getId();
                break;
            case "machinetype":
                result.maschNr = this.props.entity.Maschtyp;
                result.maschTyp = this.props.entity.Maschtyp;
                break;
            case "assignment":
                if (this.props.isResourceSearch === true && Helper.hasValue(this.props.context) && Helper.hasValue(this.props.context.projNr) && this.props.context.projNr !== "") {
                    result.maschNr = this.props.context.projNr;
                    result.pos = this.props.context.pIndex;
                } else {
                    result.maschNr = Helper.hasValue(this.props.entity.ProjNr) ? this.props.entity.ProjNr : this.props.queryParameters.projnr;
                    result.pos = Helper.hasValue(this.props.entity.PIndex) ? this.props.entity.PIndex : this.props.queryParameters.pindex;
                }
                break;
            default:
                throw new Error("Unsupported skills running context: " + this.props.runningContext);
        }

        return result;
    }

    private _saveSkills() {
        this.treeRef.saveSkills();
    }

    private _triggerChange() {
        if (Helper.hasValue(this.props.onChange))
            this.props.onChange({ model: "" }, null, null);
    }

    private _changeProfile = (profileId) => {
        let pos = this._pos;
        if (typeof pos === "string")
            pos = parseInt(pos) + 1000;
        if (Helper.hasValue(profileId) && Helper.hasValue(this.props.queryParameters) && Helper.hasValue(this.props.queryParameters.projnr))
            profileId = this.props.queryParameters.projnr;
        if (Helper.hasValue(profileId) && Helper.hasValue(pos)) {
            HTTPService.get<Array<SkillCategory>>("Skills/GetAssignmentSkills?maschnr=" + profileId + "&pos=" + (pos))
                .then((result) => {
                    if (Helper.hasValue(this.treeRef))
                        this.treeRef.mapCategoriesToTree(result);
                    this.props.entity.SkillprofileId = profileId;
                    this._skillProfile = profileId;
                    this.props.entity.SkillprofileIdIsSet = true;
                    this._triggerChange();
                });
        }
    }

    private handleCustomButtonClick = (id: string) => {
        switch (id) {
            case "skillsLoad":
                this._handleLoadSkills();
                break;
            case "import":
                this.openDialog();
                break;
            case "export":
                this.download();
                break;
        }
    }

    private _handleLoadSkills = () => {
        let url = null;
        if (this.props.runningContext.toLowerCase() === "ticket") {
            url = "Procedure/GetMergedSkills?id=" + encodeURIComponent(this._maschNr);
        } else if (this.props.runningContext.toLowerCase() === "assignment") {
            url = "ResourcePlanning/GetAssignmentMergedSkills?projNr=" + encodeURIComponent(this._maschNr) + "&pIndex=" + this._pos;
        }

        if (url !== null) {
            HTTPService.get<JsonResultClass<Array<SkillCategory>>>(url)
                .then(result => {
                    if (result.Code == 0) {
                        if (Array.isArray(result.Data) && result.Data.length > 0) {
                            if (Helper.hasValue(this.treeRef))
                                this.treeRef.mapCategoriesToTree(result.Data);
                            this._triggerChange();
                            this.setState({});
                        }
                    }
                    else {
                        NotificationHelper.showErrorMessage(result.Message);
                    }
                })
        }
    }

    private _createButtons = () => {
        let readonly = this.readOnly || !this._canWrite;
        let buttons = [];

        if (this.props.editable && (Helper.hasValue(this.props.editable)) && !readonly) {
            buttons.push(<button type="button" key={"Download"} onClick={this.download}
                className="k-button k-grid"> <i className="fal fa-file-download" style={{ marginRight: "5px" }}></i>{Helper.getTranslation("Skills.Export")}</button>)

            buttons.push(<button type="button" key={"Upload"} onClick={this.openDialog} id="skill-upload"
                className="k-button k-grid"> <i className="fal fa-file-upload" style={{ marginRight: "5px" }}></i>{Helper.getTranslation("Skills.Import")}</button>)
        }

        return (<div className="innoToolBarWrapper"> {buttons}</div>);

    }

    private _createDialog = () => {
        if (this.state.loadSkillsDialogOpen) {
            this._dialogProps.title = Helper.getTranslation("Skills.Import");
            return (
                <div>
                    <Dialog {...this._dialogProps} open={this.state.loadSkillsDialogOpen} >
                        <FileUpload style={{ width: "383px", marginLeft: "5px" }} upload={this.upload} validate={this.validateFile} uploadButton={false} onFileChange={(file) => { this.setState({ file: file }); }} />
                    </Dialog>
                </div>
            );
        } else {
            return null;
        }
    }

    private _createSkills = () => {
        let pos = this.getPos();
        let showDateSkillsAsCheckBox = pos > 1000 || pos == 13;
        let showDateSkills = pos > 1000 || pos == 1 || pos == 13;
        const disabled = this.props.disabled && Helper.hasValue(this.props.disabled);

        let filter: SkillFilter = {
            LoadSkills: true,
            RefNr: this.getId(),
            Typ: this.getPos(),
            ShowOnlyCatsWithSkills: !this.props.dynamicSearch,
            ShowOnlyCatsWithValues: false,
            Text: "",
            FilterByOgrNr: Helper.getValueOrDefault(this.props.dynamicSearch, true),
            WithDateSkills: showDateSkills
        };

        let isResourceContext = Helper.getValueOrDefault((this.props.runningContext.toLowerCase() == "resource"), false);
        let skillEditBoxProps: SkillEditBoxProps = {
            RecordRight: this.props.entity.RecordRight,
            readOnly: this.readOnly || !this._canWrite,
            other: this.props.other,
            showDateAsCheckBox: showDateSkillsAsCheckBox,
            useTargetValues: isResourceContext,
            usePriority: isResourceContext
        };

        return (<>
            <SkillTreeApp skillSettings={this._skillSettings} skillEditBoxProps={skillEditBoxProps}
                skillFilter={filter} ref={(ref) => { this.treeRef = ref; }} onDataLoaded={this.onTreeDataLoaded}
                onDataChanged={this.onTreeDataChanged} loadOnDemand={this.props.dynamicSearch} disabled={disabled} renderWithRegions={!this.props.dynamicSearch} />
            {this._createDialog()}
        </>);
    }

    private onTreeDataLoaded = (data: TreeNodeVm[]) => {
        if (Helper.hasValue(this.props.onTreeDataLoaded))
            this.props.onTreeDataLoaded(data);
    }

    private onTreeDataChanged = (data: TreeNodeVm[]) => {
        if (Helper.hasValue(this.props.onTreeDataChanged))
            this.props.onTreeDataChanged(data);
    }

    openDialog = () => {
        this.setState({ loadSkillsDialogOpen: true });
    }

    validateFile = (file: any) => {
        const format = ".xl";
        const dataFormat = file.extension;
        const isValid = dataFormat.indexOf(format) > -1;


        if (!isValid) {
            NotificationHelper.showErrorMessage(Helper.getTranslation("Skills.InvalidDataFormat"));
        }
        return isValid;
    }

    getPos(): number {
        let pos = 0;
        switch (this.props.runningContext.toLowerCase()) {
            case ("resource"):
                pos = SkillHelper.getRefPos("resource");
                break;
            case ("address"):
                pos = SkillHelper.getRefPos("address");
                break;
            case ("machine"):
                pos = SkillHelper.getRefPos("machine");
                break;
            case ("machinetype"):
                pos = SkillHelper.getRefPos("machinetype");
                break;
            case ("ticket"):
                pos = SkillHelper.getRefPos("ticket");
                break;
            case ("project"):
                pos = SkillHelper.getRefPos("project");
                break;
            case ("assignment"):
                let index = Helper.hasValue(this._pos) ? this._pos : this.props.queryParameters.pindex;
                pos = SkillHelper.getRefPos("assignment", parseInt(index));
                break;
        }
        return pos;
    }

    getId(): string {
        let e: EntitySkills = this.props.entity;
        let id;
        switch (this.props.runningContext.toLowerCase()) {
            case ("resource"):
                id = e.Ressnr;
                break;
            case ("address"):
                id = e.AdrNr;
                break;
            case ("machine"):
                id = e.MaschNr;
                break;
            case ("machinetype"):
                id = e.Maschtyp;
                break;
            case ("ticket"):
                id = e.ProblemNr;
                break;
            case ("project"):
                id = e.Projnr;
                break;
            case ("assignment"):
                id = Helper.hasValue(e.ProjNr) ? e.ProjNr : this.props.queryParameters.projnr;
                break;
        }
        return id;
    }

    download = () => {
        let id = this.getId();
        let pos = this.getPos();

        if (id != undefined && pos != undefined) {
            Helper.download(Config.baseUrl + "Skills/GetExportFile?Id=" + id + "&pos=" + pos, null);
        }

    }

    upload = (file: any) => {
        if (file !== null) {
            let id = this.getId();
            let pos = this.getPos();

            const formData = new FormData();
            formData.append("file", file.rawFile);
            formData.append("id", id);
            formData.append("pos", pos.toString());

            HTTPService.post<JsonResultClass<SkillCategory[]>>("Skills/Upload", formData)
                .then(result => {
                    if (result.Code === 1) {
                        NotificationHelper.showErrorMessage(result.Message);
                    } else {
                        this.treeRef.mapCategoriesToTree(result.Data);
                        this._triggerChange();
                        this.setState({});
                    }

                });
        }
    }

    clearValues = () => {
        this.treeRef.clearAllSkills();
    }

    getTreeNodeData = (): TreeNodeVm[] => {
        if (Helper.hasValue(this.treeRef)) {
            return this.treeRef.getTreeNodeData();
        }
    }

    getSkills: any = (): SkillCategory[] => {
        let result: SkillCategory[] = [];
        let treeNodeData = this.getTreeNodeData();
        result = SkillHelper.getAllSkillCategories(treeNodeData);
        return result;
    }

    notifyOnFirstLoad = (callback: (skills: Array<SkillCategory>) => void) => {
        if (Helper.hasValue(callback)) {
            callback(this.getSkills());
        } else {
            this._afterLoadCallback = null;
        }
    }

    private _initApp = () => {
        const maschPos = this._getMaschNr();
        this._maschNr = maschPos.maschNr;
        if (Helper.hasValue(maschPos.pos)) {
            this._pos = maschPos.pos;
        }

        this._useSkillProfile = this.props.useSkillProfile === true;
        HTTPService.get("Skills/GetSettings?" + (this.props.runningContext === "resource" ? Helper.buildQueryParameter("ressnr", this._maschNr) : Helper.buildQueryParameter("context", this.props.runningContext)))
            .then((result) => {
                this._filledSkillColor = result.skillColor || this._filledSkillColor;
                this._canWrite = result.canWrite;
                this.readOnly = (this.readOnly || !result.canEdit)
                this._canLoadSkills = result.canLoad && Helper.getValueOrDefault(!this.props.dynamicSearch, true);
                this._skillSettings = result.skillSettings;
                if (this.props.isResourceSearch) {
                    this._canWrite = true;
                } else if (this.props.runningContext === "assignment") {
                    this._canWrite = this.props.entity.RecordRight.CanWriteSkills;
                }

                this._skillsLoaded = true;
                this.setState({}, () => {
                    if (this._internalAfterLoadCallback) {
                        this._internalAfterLoadCallback();
                    }

                    if (Helper.hasValue(this._afterLoadCallback)) {
                        this._afterLoadCallback(this.getSkills());
                        this._afterLoadCallback = null;
                    }
                });
            });

        $(window).on("saveSkills", (e, entity) => {
            if (this.props.entity === entity) {
                this._saveSkills();
            }
        });

        if (this._useSkillProfile && Helper.hasValue(this.props.entity.SkillprofileId)) {
            this._skillProfile = this.props.entity.SkillprofileId;
        }
    }

    componentDidUpdate() {
        const maschPos = this._getMaschNr();
        if (this._maschNr !== maschPos.maschNr) {
            this._maschNr = maschPos.maschNr;
            if (Helper.hasValue(maschPos.pos)) {
                this._pos = maschPos.pos;
            }
        }
    }

    componentWillUnmount() {
        if (this._matrixApp !== null && Helper.hasValue(this.props.skillsMatrixDOMElement)) {
            ReactDOM.unmountComponentAtNode(this.props.skillsMatrixDOMElement);
        }
    }

    private _getSkillProfiles = () => {
        let hideProfile = !Helper.hasValue(this._maschNr) || this.props.runningContext !== "assignment" || this.dynamicSearch;
        if (!this._useSkillProfile || hideProfile)
            return null;

        const profileProps: DropDownListProps = {
            field: {
                dataTextField: "Name",
                dataValueField: "Value",
                model: "skillProfile",
                type: "n/a",
                dataSourceUrl: "Skills/GetRequirementProfiles"
            },
            commandManager: CommandManager.create(),
            value: this._skillProfile?.toString(),
            onDataLoaded: (_, data: []) => {
                if (!Helper.hasValue(data) || data.length === 0) {
                    this._useSkillProfile = false;
                    this.setState({})
                }
            },
            onChange: (f, v, d) => {
                this._changeProfile(v);
            },
            dataSource: [],
            disabled: !(this.props.entity.RecordRight.CanWriteSkills || this.props.entity.RecordRight.CanChangeSkillprofile)
        };
        return <FieldInstance label={Helper.getTranslation("Ressource.Anforderungsprofil")} labelStyle={{ width: "auto" }}>
            <div style={{ maxWidth: 350 }}>
                <DropDownList {...profileProps} />
            </div>
        </FieldInstance>
    }

    render() {
        let content = null;

        if (this._skillsLoaded) {
            let hideButton = !Helper.hasValue(this._maschNr);
            const customToolbarButtons: Array<ToolBarCustomButton> = hideButton ? null : [{
                id: "skillsLoad",
                caption: Helper.getTranslation("WebSMS.Skills.BtnMergeFrom"),
                enabled: true,
                iconClass: "k-i-seek-s"
            }];
            if (Helper.useMobileGui()) {
                customToolbarButtons.push({
                    id: "import",
                    caption: Helper.getTranslation("Skills.Import"),
                    enabled: true,
                    iconClass: "fal fa-file-upload fa-fw"
                });
                customToolbarButtons.push({
                    id: "export",
                    caption: Helper.getTranslation("Skills.Export"),
                    enabled: true,
                    iconClass: "fal fa-file-download fa-fw"
                });
            }


            content = <FullHeightContainer classes="skills-wrapper">
                {this._canLoadSkills ?
                    <ToolBar
                        customButtons={customToolbarButtons}
                        handleCustomButtonClick={this.handleCustomButtonClick}
                        useFloatingButtons={Helper.useMobileGui()}
                    />
                    : null
                }
                {Helper.hasValue(this.props.title) ? <label>{this.props.title}</label> : null}
                {this._getSkillProfiles()}
                {/*this._createButtons()*/}
                {this._createSkills()}
                {
                    Helper.useMobileGui() ? null : <FloatingsButtons onImportClick={this.openDialog} onExportClick={this.download} />
                }
            </FullHeightContainer>
        }

        return (
            content
        );
    }
}

export interface Translation {
    fid: number,
    language: string,
    value: string
}

export interface LanguageData {
    languages: string,
    fid: number,
    rIndex?: number
}

class FloatingsButtons extends React.Component<FloatingsButtonsProps, any> {

    constructor(props) {
        super(props)
    }

    private createImportButton = () => {
        return (
            <div className="sheet__action" style={{ backgroundColor: "#cc3467" }} title={Helper.getTranslation("Skills.Import")} onClick={this.props.onImportClick} >
                <i className="fal fa-file-upload fa-fw fa-2x"></i>
            </div>
        );
    }

    private createExportButton = () => {
        return (
            <div className="sheet__action" style={{ backgroundColor: "#cc3467" }} title={Helper.getTranslation("Skills.Export")} onClick={this.props.onExportClick} >
                <i className="fal fa-file-download fa-fw fa-2x"></i>
            </div>
        );
    }

    render() {
        return (
            <div className="sheet__actions sheet__actions--active">
                {this.createImportButton()}
                {this.createExportButton()}
            </div>
        );
    }
}

interface FloatingsButtonsProps {
    onImportClick: () => void;
    onExportClick: () => void;
}

export interface SkillsProps {
    entity: EntitySkills;
    onChange?(field: { model: string }, value: any, dataItem?: any);
    runningContext: string;
    useSkillProfile?: boolean;
    skillsMatrixDOMElement?: HTMLElement;
    disableSkillMatrixSelect?: boolean;
    isWindow?: boolean;
    isResourceSearch?: boolean;
    context?: {
        projNr?: string,
        pIndex?: number,
        contextType?: string
    };
    title?: string;
    editable?: boolean;
    disabled?: boolean;
    dynamicSearch?: boolean;
    other?: { name: string, value: any }[];
    queryParameters?: any;
    onTreeDataLoaded?: (data: TreeNodeVm[]) => void;
    onTreeDataChanged?: (data: TreeNodeVm[]) => void;
}

interface EntitySkills extends Entity {
    Ressnr?: string;
    Maschnr?: string;
    MaschNr?: string;
    AdrNr?: string;
    ProblemNr?: string;
    Maschtyp?: string;
    Pos?: number;
    ProjNr?: string;
    Projnr?: string;
    PIndex?: number;
    SkillprofileId?: string;
    SkillprofileIdIsSet?: boolean;
    Ogrnr?: number;
    RecordRight: {
        CanRead: boolean;
        CanWrite: boolean;
        ResourceSkillsRight?: boolean;
        CanReadSkills: boolean;
        CanWriteSkills: boolean;
        CanChangeSkillprofile: boolean;
    }
}